import React, { useState, useEffect, useCallback } from "react";
import { useAuth, api } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { toast } from "sonner";
import { format } from "date-fns";
import { 
  FileText, ArrowLeft, Loader2, Filter,
  FileDown, Trash2, Receipt, Bell, Calendar, Plus
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import CreditNotesSection from "../components/admin/CreditNotesSection";
import { InvoiceCard } from "../components/invoices/InvoiceCard";
import { CreateInvoiceModal } from "../components/invoices/CreateInvoiceModal";
import { EditInvoiceModal } from "../components/invoices/EditInvoiceModal";
import { MonthlySummaryModal } from "../components/invoices/MonthlySummaryModal";

const InvoicesPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedInvoices, setSelectedInvoices] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showMonthlySummaryModal, setShowMonthlySummaryModal] = useState(false);
  const [creatingInvoice, setCreatingInvoice] = useState(false);
  const [uninvoicedBookings, setUninvoicedBookings] = useState([]);
  const [loadingBookings, setLoadingBookings] = useState(false);
  const [newInvoice, setNewInvoice] = useState({
    client_id: "",
    booking_ids: [],
    due_date: format(new Date(Date.now() + 14 * 24 * 60 * 60 * 1000), "yyyy-MM-dd"),
    billing_period_from: "",
    billing_period_to: "",
    payment_method: "bank_transfer",
    notes: ""
  });
  const [editInvoice, setEditInvoice] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCreditNotesModal, setShowCreditNotesModal] = useState(false);
  const [selectedInvoiceForCreditNote, setSelectedInvoiceForCreditNote] = useState(null);
  const [sendingReminders, setSendingReminders] = useState(false);
  const [monthlySummary, setMonthlySummary] = useState({
    client_id: "",
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear()
  });

  const fetchInvoices = useCallback(async () => {
    try {
      const params = {};
      if (statusFilter !== "all") params.status = statusFilter;
      const response = await api.get("/invoices", { params });
      setInvoices(response.data);
    } catch (error) {
      console.error("Error fetching invoices:", error);
      toast.error("Failed to load invoices");
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  const fetchClients = async () => {
    try {
      const response = await api.get("/users", { params: { role: "client" } });
      setClients(response.data);
    } catch (error) {
      console.error("Error fetching clients:", error);
    }
  };

  const fetchUninvoicedBookings = async (clientId) => {
    if (!clientId) { setUninvoicedBookings([]); return; }
    setLoadingBookings(true);
    try {
      const response = await api.get("/bookings/uninvoiced", { params: { client_id: clientId } });
      setUninvoicedBookings(response.data);
    } catch (error) {
      try {
        const allBookings = await api.get("/bookings", { params: { user_id: clientId } });
        setUninvoicedBookings(allBookings.data.filter(b => !b.invoice_id && (b.status === "completed" || b.status === "confirmed")));
      } catch (err) { setUninvoicedBookings([]); }
    } finally { setLoadingBookings(false); }
  };

  useEffect(() => { fetchInvoices(); if (user?.role === "admin") fetchClients(); }, [fetchInvoices, user?.role]);
  useEffect(() => { if (newInvoice.client_id) { fetchUninvoicedBookings(newInvoice.client_id); setNewInvoice(prev => ({ ...prev, booking_ids: [] })); } }, [newInvoice.client_id]);

  const handleCreateInvoice = async () => {
    if (!newInvoice.client_id || newInvoice.booking_ids.length === 0) { toast.error("Please select a client and at least one booking"); return; }
    setCreatingInvoice(true);
    try {
      const payload = { ...newInvoice, due_date: newInvoice.due_date ? new Date(newInvoice.due_date).toISOString() : null, billing_period_from: newInvoice.billing_period_from ? new Date(newInvoice.billing_period_from).toISOString() : null, billing_period_to: newInvoice.billing_period_to ? new Date(newInvoice.billing_period_to).toISOString() : null };
      const response = await api.post("/invoices", payload);
      toast.success(`Invoice ${response.data.invoice_number} created`);
      setShowCreateModal(false);
      setNewInvoice({ client_id: "", booking_ids: [], due_date: format(new Date(Date.now() + 14 * 24 * 60 * 60 * 1000), "yyyy-MM-dd"), billing_period_from: "", billing_period_to: "", payment_method: "bank_transfer", notes: "" });
      fetchInvoices();
    } catch (error) { toast.error(error.response?.data?.detail || "Failed to create invoice"); } finally { setCreatingInvoice(false); }
  };

  const handleMonthlySummary = async () => {
    if (!monthlySummary.client_id) { toast.error("Please select a client"); return; }
    setCreatingInvoice(true);
    try {
      const response = await api.post("/invoices/monthly-summary", null, { params: monthlySummary });
      toast.success(`Monthly summary created: ${response.data.invoice_number} (${response.data.bookings_count} bookings)`);
      setShowMonthlySummaryModal(false);
      fetchInvoices();
    } catch (error) { toast.error(error.response?.data?.detail || "Failed to create monthly summary"); } finally { setCreatingInvoice(false); }
  };

  const handleUpdateStatus = async (invoiceId, newStatus) => {
    try { await api.patch(`/invoices/${invoiceId}/status`, null, { params: { status: newStatus } }); toast.success(`Invoice status updated to ${newStatus}`); fetchInvoices(); } catch (error) { toast.error("Failed to update invoice status"); }
  };

  const handleEditInvoice = (invoice) => {
    setEditInvoice({ id: invoice.id, subtotal: invoice.subtotal, discount_percentage: invoice.discount_percentage, total: invoice.total, due_date: invoice.due_date ? format(new Date(invoice.due_date), "yyyy-MM-dd") : "", billing_period_from: invoice.billing_period_from ? format(new Date(invoice.billing_period_from), "yyyy-MM-dd") : "", billing_period_to: invoice.billing_period_to ? format(new Date(invoice.billing_period_to), "yyyy-MM-dd") : "", notes: invoice.notes || "", adjustment_reason: "" });
    setShowEditModal(true);
  };

  const handleSaveInvoiceEdit = async () => {
    if (!editInvoice) return;
    try {
      await api.put(`/invoices/${editInvoice.id}`, { subtotal: parseFloat(editInvoice.subtotal), discount_percentage: parseFloat(editInvoice.discount_percentage || 0), total: parseFloat(editInvoice.total), due_date: editInvoice.due_date ? new Date(editInvoice.due_date).toISOString() : null, billing_period_from: editInvoice.billing_period_from ? new Date(editInvoice.billing_period_from).toISOString() : null, billing_period_to: editInvoice.billing_period_to ? new Date(editInvoice.billing_period_to).toISOString() : null, notes: editInvoice.notes, adjustment_reason: editInvoice.adjustment_reason });
      toast.success("Invoice updated successfully"); setShowEditModal(false); setEditInvoice(null); fetchInvoices();
    } catch (error) { toast.error(error.response?.data?.detail || "Failed to update invoice"); }
  };

  const handleDownloadPdf = async (invoiceId, invoiceNumber) => {
    try {
      const response = await api.get(`/invoices/${invoiceId}/pdf`, { responseType: 'blob' });
      if (response.data.type === 'application/json') { const text = await response.data.text(); toast.error(JSON.parse(text).detail || "Failed to generate PDF"); return; }
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a'); link.href = url; link.setAttribute('download', `${invoiceNumber}.pdf`); document.body.appendChild(link); link.click(); link.remove(); window.URL.revokeObjectURL(url);
      toast.success("PDF downloaded successfully");
    } catch (error) { toast.error(error.response?.data?.detail || "Failed to download PDF"); }
  };

  const handleSendInvoice = async (invoiceId) => { try { await api.post(`/invoices/${invoiceId}/send`); toast.success("Invoice sent to client"); fetchInvoices(); } catch (error) { toast.error(error.response?.data?.detail || "Failed to send invoice"); } };

  const handlePayOnline = async (invoice) => {
    if (invoice.status === "paid") { toast.error("Invoice already paid"); return; }
    try { const response = await api.post(`/invoices/${invoice.id}/pay-online`, { origin_url: window.location.origin }); window.location.href = response.data.checkout_url; } catch (error) { toast.error(error.response?.data?.detail || "Failed to initiate payment"); }
  };

  const handleSendReminders = async () => {
    setSendingReminders(true);
    try { const response = await api.post("/invoices/send-reminders"); toast.success(`Sent ${response.data.reminders_sent} reminder(s): ${response.data.overdue_count} overdue, ${response.data.upcoming_count} upcoming`, { duration: 5000 }); } catch (error) { toast.error("Failed to send reminders"); } finally { setSendingReminders(false); }
  };

  const handleDeleteInvoice = async (invoiceId) => {
    if (!window.confirm("Are you sure you want to delete this invoice?")) return;
    try { await api.delete(`/invoices/${invoiceId}`); toast.success("Invoice deleted successfully"); fetchInvoices(); } catch (error) { toast.error(error.response?.data?.detail || "Failed to delete invoice"); }
  };

  const handleBulkDelete = async () => {
    if (selectedInvoices.length === 0) { toast.error("Please select invoices to delete"); return; }
    if (!window.confirm(`Are you sure you want to delete ${selectedInvoices.length} invoice(s)? Paid invoices will be skipped.`)) return;
    try { const response = await api.post("/invoices/bulk-delete", selectedInvoices); toast.success(response.data.message); if (response.data.errors?.length > 0) response.data.errors.forEach(err => toast.error(err)); setSelectedInvoices([]); fetchInvoices(); } catch (error) { toast.error("Failed to delete invoices"); }
  };

  const handleBulkExport = async () => {
    if (selectedInvoices.length === 0) { toast.error("Please select invoices to export"); return; }
    try { const response = await api.post("/invoices/bulk-export", selectedInvoices, { responseType: 'blob' }); const url = window.URL.createObjectURL(new Blob([response.data])); const link = document.createElement('a'); link.href = url; link.setAttribute('download', `invoices_export_${format(new Date(), 'yyyyMMdd')}.zip`); document.body.appendChild(link); link.click(); link.remove(); window.URL.revokeObjectURL(url); toast.success(`Exported ${selectedInvoices.length} invoices`); setSelectedInvoices([]); } catch (error) { toast.error("Failed to export invoices"); }
  };

  const toggleSelectInvoice = (invoiceId) => { setSelectedInvoices(prev => prev.includes(invoiceId) ? prev.filter(id => id !== invoiceId) : [...prev, invoiceId]); };
  const toggleSelectAll = () => { setSelectedInvoices(selectedInvoices.length === invoices.length ? [] : invoices.map(inv => inv.id)); };

  if (loading) return <div className="min-h-screen bg-background flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;

  return (
    <div className="min-h-screen bg-background">
      <header className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2 sm:gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate("/admin")} data-testid="back-btn"><ArrowLeft className="h-5 w-5" /></Button>
              <div>
                <h1 className="font-semibold text-sm sm:text-base">Invoices</h1>
                <p className="text-xs text-muted-foreground">{invoices.length} total</p>
              </div>
            </div>
            <div className="flex items-center gap-1 sm:gap-2 overflow-x-auto">
              {selectedInvoices.length > 0 && (
                <>
                  <Button onClick={handleBulkExport} variant="outline" size="sm" data-testid="bulk-export-btn" className="hidden sm:flex"><FileDown className="h-4 w-4 mr-2" />Export ({selectedInvoices.length})</Button>
                  <Button onClick={handleBulkDelete} variant="destructive" size="sm" data-testid="bulk-delete-btn"><Trash2 className="h-4 w-4 sm:mr-2" /><span className="hidden sm:inline">Delete ({selectedInvoices.length})</span></Button>
                </>
              )}
              <Button onClick={handleSendReminders} variant="outline" size="sm" data-testid="send-reminders-btn" className="hidden lg:flex" disabled={sendingReminders}>
                {sendingReminders ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Bell className="h-4 w-4 mr-2" />}Reminders
              </Button>
              <Button onClick={() => setShowMonthlySummaryModal(true)} variant="outline" size="sm" data-testid="monthly-summary-btn" className="hidden md:flex"><Calendar className="h-4 w-4 mr-2" />Summary</Button>
              <Button onClick={() => setShowCreateModal(true)} size="sm" data-testid="create-invoice-btn"><Plus className="h-4 w-4 sm:mr-2" /><span className="hidden sm:inline">Create</span></Button>
            </div>
          </div>
        </div>
      </header>

      <main className="pt-20 pb-8 px-4 max-w-7xl mx-auto">
        <Card className="dashboard-card mb-6">
          <CardContent className="py-4">
            <div className="flex items-center gap-4">
              <Filter className="h-4 w-4 text-muted-foreground" />
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[180px] bg-secondary/50 border-black/10" data-testid="status-filter"><SelectValue placeholder="Filter by status" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="sent">Sent</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                  <SelectItem value="overdue">Overdue</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
              <div className="ml-auto flex items-center gap-2">
                <input type="checkbox" checked={selectedInvoices.length === invoices.length && invoices.length > 0} onChange={toggleSelectAll} className="h-4 w-4 rounded border-white/20 bg-secondary" />
                <span className="text-sm text-muted-foreground">Select All</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="space-y-4" data-testid="invoice-list">
          {invoices.length === 0 ? (
            <Card className="dashboard-card"><CardContent className="py-12 text-center"><FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" /><p className="text-lg font-medium">No invoices found</p><p className="text-sm text-muted-foreground">Create your first invoice to get started</p></CardContent></Card>
          ) : (
            invoices.map(invoice => (
              <InvoiceCard
                key={invoice.id}
                invoice={invoice}
                isSelected={selectedInvoices.includes(invoice.id)}
                onToggleSelect={toggleSelectInvoice}
                onPayOnline={handlePayOnline}
                onSendInvoice={handleSendInvoice}
                onDownloadPdf={handleDownloadPdf}
                onUpdateStatus={handleUpdateStatus}
                onEditInvoice={handleEditInvoice}
                onOpenCreditNotes={(inv) => { setSelectedInvoiceForCreditNote(inv); setShowCreditNotesModal(true); }}
                onDeleteInvoice={handleDeleteInvoice}
              />
            ))
          )}
        </div>
      </main>

      <EditInvoiceModal open={showEditModal} onOpenChange={setShowEditModal} editInvoice={editInvoice} setEditInvoice={setEditInvoice} onSaveEdit={handleSaveInvoiceEdit} />
      <CreateInvoiceModal open={showCreateModal} onOpenChange={setShowCreateModal} clients={clients} newInvoice={newInvoice} setNewInvoice={setNewInvoice} uninvoicedBookings={uninvoicedBookings} loadingBookings={loadingBookings} creatingInvoice={creatingInvoice} onCreateInvoice={handleCreateInvoice} />
      <MonthlySummaryModal open={showMonthlySummaryModal} onOpenChange={setShowMonthlySummaryModal} clients={clients} monthlySummary={monthlySummary} setMonthlySummary={setMonthlySummary} creatingInvoice={creatingInvoice} onGenerateSummary={handleMonthlySummary} />

      <Dialog open={showCreditNotesModal} onOpenChange={setShowCreditNotesModal}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Receipt className="h-5 w-5 text-primary" />Credit Notes - {selectedInvoiceForCreditNote?.invoice_number}</DialogTitle>
          </DialogHeader>
          {selectedInvoiceForCreditNote && (
            <CreditNotesSection invoiceId={selectedInvoiceForCreditNote.id} invoiceNumber={selectedInvoiceForCreditNote.invoice_number} invoiceTotal={selectedInvoiceForCreditNote.total} invoiceStatus={selectedInvoiceForCreditNote.status} onCreditNoteCreated={fetchInvoices} />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InvoicesPage;
